import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  where
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import {
  stripRssBoilerplate,
  formatReadOnHostname,
  truncateSummaryForDisplay
} from './newsUtils';
import {
  resolveMakeToCanonicalKey,
  getMakeQueryBatchesForCanonicalKey
} from '../utils/brandResolve';
import './NewsPage.css';
import { isPedalNewsUiEnabled } from '../featureFlags';

/** Same idea as Latest Pedals: restore scroll + UI after returning from listing/search. */
const NEWS_PAGE_STATE_KEY = 'pedalNews_pageState';
const NEWS_STATE_TTL_MS = 30 * 60 * 1000;

function readInitialNewsPageState(locationState) {
  if (locationState?.fresh) {
    return { sourceFilter: 'all', scrollY: null };
  }
  try {
    const raw = sessionStorage.getItem(NEWS_PAGE_STATE_KEY);
    if (!raw) return { sourceFilter: 'all', scrollY: null };
    const s = JSON.parse(raw);
    if (Date.now() - (s.timestamp || 0) > NEWS_STATE_TTL_MS) {
      return { sourceFilter: 'all', scrollY: null };
    }
    return {
      sourceFilter: typeof s.sourceFilter === 'string' ? s.sourceFilter : 'all',
      scrollY: typeof s.scrollY === 'number' ? s.scrollY : null
    };
  } catch {
    return { sourceFilter: 'all', scrollY: null };
  }
}

const ARTICLE_LIMIT = 120;
const LISTINGS_FETCH_CAP = 15;
const LISTINGS_SHOW = 6;

/** Matches `sourceKey` from RSS ingest — client-side filter only. */
const SOURCE_FILTER_OPTIONS = [
  { key: 'all', label: 'All sources' },
  { key: 'delicious-audio', label: 'Delicious Audio' },
  { key: 'gear-news-guitar', label: 'Gear News' },
  { key: 'premier-guitar', label: 'Premier Guitar' },
  { key: 'guitarpedalx', label: 'GuitarPedalX' }
];

const CARD_W = 118;

/** RSS hero image — left column when present. */
function NewsArticleThumb({ imageUrl, articleLink }) {
  const [show, setShow] = useState(!!imageUrl);
  if (!imageUrl || !show) return null;
  return (
    <a
      href={articleLink}
      target="_blank"
      rel="noopener noreferrer"
      className="news-article-thumb-wrap"
    >
      <img
        src={imageUrl}
        alt=""
        loading="lazy"
        decoding="async"
        className="news-article-thumb-img"
        onError={() => setShow(false)}
      />
    </a>
  );
}

function listingThumbUrl(listing) {
  const img = listing.images;
  if (!img || typeof img !== 'object') return null;
  return (
    img.top ||
    img.back ||
    img.upper ||
    img.lower ||
    img.detail1 ||
    img.detail2 ||
    null
  );
}

function formatListingPrice(price) {
  if (price === undefined || price === null || price === '') return '—';
  const n = Number(price);
  if (isNaN(n)) return '—';
  return `$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}`;
}

function ListingMiniCard({ listing, onBeforeNavigate }) {
  const [imgOk, setImgOk] = useState(true);
  const thumb = listingThumbUrl(listing);
  return (
    <Link
      to={`/listing/${listing.id}`}
      className="news-listing-card-link"
      onClick={() => onBeforeNavigate?.()}
      style={{
        borderRadius: 8,
        border: '1px solid #e8e4dc',
        backgroundColor: '#fff',
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
      }}
    >
      <div
        style={{
          width: CARD_W,
          height: CARD_W,
          maxHeight: CARD_W,
          backgroundColor: '#eef1f4',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          borderBottom: '1px solid #f3f4f6'
        }}
      >
        {thumb && imgOk ? (
          <img
            src={thumb}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block'
            }}
            loading="lazy"
            onError={() => setImgOk(false)}
          />
        ) : (
          <span style={{ color: '#9ca3af', fontSize: 11, padding: 4, textAlign: 'center' }}>
            No photo
          </span>
        )}
      </div>
      <div className="news-listing-card-meta">
        <div className="news-listing-model" title={listing.model || ''}>
          {listing.model || '—'}
        </div>
        <div className="news-listing-price">{formatListingPrice(listing.price)}</div>
      </div>
    </Link>
  );
}

/** Avoid "JHS Pedals pedals" — canonical names often already end with Pedal(s). */
function brandPedalWord(displayName) {
  const d = (displayName || '').trim();
  if (!d) return 'pedals';
  if (/pedals?$/i.test(d)) return '';
  return ' pedals';
}

function BrandListingsCarousel({ stat, listings, displayName, onBeforeNavigate }) {
  const listed = typeof stat.activeListingCount === 'number' ? stat.activeListingCount : 0;
  const members = typeof stat.wantingMemberCount === 'number' ? stat.wantingMemberCount : 0;
  const pedalWord = brandPedalWord(displayName);

  return (
    <div style={{ marginBottom: 0 }}>
      <div className="news-pedal-strip-header">
        <div className="news-pedal-strip-top-line">
          <span className="news-pedal-strip-pxc-badge">PXC</span>
          <span className="news-pedal-strip-headline">
            <span className="news-pedal-strip-brand">{displayName}</span>
            <span className="news-pedal-strip-headline-rest">
              {pedalWord} available to trade
            </span>
          </span>
        </div>
        <div className="news-pedal-strip-bottom-row">
          <span className="news-pedal-strip-substats">
            {listed} listed · {members}{' '}
            <span className="news-pedal-strip-looking-desktop">
              {members === 1 ? 'member' : 'members'} looking
            </span>
            <span className="news-pedal-strip-looking-mobile">want</span>
          </span>
          <Link
            to={`/search?q=${encodeURIComponent(displayName)}`}
            className="news-see-all-link news-pedal-strip-see-all"
            title={`See all ${displayName}`}
            onClick={() => onBeforeNavigate?.()}
          >
            <span className="news-pedal-strip-see-all-label news-pedal-strip-see-all-label--full">
              See all {displayName} →
            </span>
            <span className="news-pedal-strip-see-all-label news-pedal-strip-see-all-label--short">
              See all →
            </span>
          </Link>
        </div>
      </div>
      <div className="news-carousel-track">
        {listings.map((listing) => (
          <ListingMiniCard key={listing.id} listing={listing} onBeforeNavigate={onBeforeNavigate} />
        ))}
      </div>
    </div>
  );
}

function toJsDate(publishedAt) {
  if (!publishedAt) return null;
  try {
    const d =
      typeof publishedAt.toDate === 'function'
        ? publishedAt.toDate()
        : new Date(publishedAt);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function formatPublishedDate(publishedAt) {
  const d = toJsDate(publishedAt);
  if (!d) return '';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

function sortListingDocsByCreatedDesc(docs) {
  return docs.sort((a, b) => {
    const ta = a.createdAt?.toMillis?.() ?? a.createdAt?.seconds ?? 0;
    const tb = b.createdAt?.toMillis?.() ?? b.createdAt?.seconds ?? 0;
    return tb - ta;
  });
}

const NewsPage = () => {
  const location = useLocation();
  const canonicalUrl = `https://pedalxchange.com${location.pathname || '/news'}`;
  const savedOnMount = useRef(readInitialNewsPageState(location.state));
  const pendingScrollY = useRef(savedOnMount.current.scrollY);
  const isRestoringScroll = useRef(false);

  const [user, setUser] = useState(auth.currentUser);
  const [authLoading, setAuthLoading] = useState(true);
  const [articles, setArticles] = useState([]);
  const [brandStatsMap, setBrandStatsMap] = useState({});
  const [listingsByBrandKey, setListingsByBrandKey] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sourceFilter, setSourceFilter] = useState(() => savedOnMount.current.sourceFilter);
  const [showBackToTop, setShowBackToTop] = useState(false);

  const persistNewsPageState = useCallback(() => {
    if (isRestoringScroll.current) return;
    try {
      sessionStorage.setItem(
        NEWS_PAGE_STATE_KEY,
        JSON.stringify({
          scrollY: window.scrollY,
          sourceFilter,
          timestamp: Date.now()
        })
      );
    } catch {
      /* ignore quota / private mode */
    }
  }, [sourceFilter]);

  useLayoutEffect(() => {
    if (location.state?.fresh) {
      try {
        sessionStorage.removeItem(NEWS_PAGE_STATE_KEY);
      } catch {
        /* ignore */
      }
      pendingScrollY.current = null;
      window.scrollTo(0, 0);
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location.key, location.state?.fresh, location.pathname]);

  useLayoutEffect(() => {
    if (loading) return;
    const y = pendingScrollY.current;
    if (y == null) return;
    isRestoringScroll.current = true;
    const run = () => window.scrollTo(0, y);
    const t1 = setTimeout(run, 100);
    const t2 = user ? setTimeout(run, 450) : null;
    const t3 = setTimeout(() => {
      isRestoringScroll.current = false;
      pendingScrollY.current = null;
    }, 550);
    return () => {
      clearTimeout(t1);
      if (t2) clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [loading, user]);

  useEffect(() => {
    let throttleTimer;
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
      if (isRestoringScroll.current) return;
      clearTimeout(throttleTimer);
      throttleTimer = setTimeout(persistNewsPageState, 150);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(throttleTimer);
    };
  }, [persistNewsPageState]);

  useEffect(() => {
    if (loading || isRestoringScroll.current) return;
    persistNewsPageState();
  }, [loading, sourceFilter, persistNewsPageState]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isRestoringScroll.current) return;
      try {
        sessionStorage.setItem(
          NEWS_PAGE_STATE_KEY,
          JSON.stringify({
            scrollY: window.scrollY,
            sourceFilter,
            timestamp: Date.now()
          })
        );
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [sourceFilter]);

  useEffect(() => {
    return () => {
      const path = window.location.pathname || '';
      const goingToListing = path.startsWith('/listing/');
      const goingToSearch = path.startsWith('/search');
      if (!path.includes('/news') && !goingToListing && !goingToSearch) {
        try {
          sessionStorage.removeItem(NEWS_PAGE_STATE_KEY);
        } catch {
          /* ignore */
        }
      }
    };
  }, [location]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((u) => {
      setUser(u);
      setAuthLoading(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!isPedalNewsUiEnabled()) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const articlesSnap = await getDocs(
          query(
            collection(db, 'pedalNewsArticles'),
            orderBy('publishedAt', 'desc'),
            limit(ARTICLE_LIMIT)
          )
        );

        if (cancelled) return;

        const rows = articlesSnap.docs.map((d) => ({
          id: d.id,
          ...d.data()
        }));
        setArticles(rows);

        const stats = {};
        try {
          const statsSnap = await getDocs(collection(db, 'brandStats'));
          statsSnap.forEach((docSnap) => {
            stats[docSnap.id] = docSnap.data();
          });
        } catch (statsErr) {
          console.warn('News page: brandStats unreadable, brand footers hidden:', statsErr);
        }
        if (!cancelled) setBrandStatsMap(stats);
      } catch (e) {
        console.error('News load error:', e);
        if (!cancelled) setError(e.message || 'Failed to load news.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!user || loading) return;

    const keys = new Set();
    articles.forEach((a) => {
      (a.extractedBrands || []).forEach((k) => keys.add(k));
    });

    if (keys.size === 0) {
      setListingsByBrandKey({});
      return;
    }

    let cancelled = false;

    (async () => {
      const out = {};
      await Promise.all(
        [...keys].map(async (canonicalKey) => {
          const stat = brandStatsMap[canonicalKey];
          if (!stat?.displayName) {
            out[canonicalKey] = [];
            return;
          }
          try {
            /** `brandStats` counts listings by resolved canonical key (aliases). Querying only
             * `make == displayName` misses rows stored as e.g. "Chase Bliss Audio". */
            const batches = getMakeQueryBatchesForCanonicalKey(canonicalKey);
            const idToRow = new Map();
            for (const batch of batches) {
              if (!batch.length) continue;
              const q = query(
                collection(db, 'listings'),
                where('active', '==', true),
                where('make', 'in', batch),
                limit(LISTINGS_FETCH_CAP)
              );
              const snap = await getDocs(q);
              snap.docs.forEach((d) => {
                if (idToRow.has(d.id)) return;
                const row = { id: d.id, ...d.data() };
                if (resolveMakeToCanonicalKey(row.make || '') !== canonicalKey) return;
                idToRow.set(d.id, row);
              });
            }
            const docs = [...idToRow.values()];
            const browseable = sortListingDocsByCreatedDesc(docs).filter(
              (d) => !d.inTrade && !d.needsReactivation
            );
            out[canonicalKey] = browseable.slice(0, LISTINGS_SHOW);
          } catch (e) {
            console.error('News brand listings:', canonicalKey, e);
            out[canonicalKey] = [];
          }
        })
      );
      if (!cancelled) setListingsByBrandKey(out);
    })();

    return () => {
      cancelled = true;
    };
  }, [user, loading, articles, brandStatsMap]);

  if (!isPedalNewsUiEnabled()) {
    return <Navigate to="/dashboard" replace />;
  }

  if (authLoading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#F7F3EB'
        }}
      >
        <p style={{ color: '#4b5563' }}>Loading...</p>
      </div>
    );
  }

  const filteredArticles =
    sourceFilter === 'all'
      ? articles
      : articles.filter((a) => a.sourceKey === sourceFilter);

  return (
    <div
      className="news-page"
      style={{
        backgroundColor: '#F7F3EB',
        minHeight: 'calc(100vh - var(--navbar-height, 125px))'
      }}
    >
      <Helmet>
        <title>Pedal News | PedalxChange</title>
        <meta
          name="description"
          content="Industry pedal news, reviews, and releases — curated from top gear sites. See what’s new, then find matching gear on PedalxChange."
        />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <div className="news-page-container" style={{ margin: '0 auto', padding: '32px 16px 64px' }}>
        <Link
          to="/dashboard"
          style={{ color: '#5A9ECB', textDecoration: 'none', marginBottom: 24, display: 'inline-block' }}
        >
          ← Back to Dashboard
        </Link>

        <header style={{ marginBottom: 40 }}>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 700, color: '#111827', marginBottom: 8, fontFamily: 'Nunito, sans-serif' }}>
            Pedal news
          </h1>
          <p style={{ color: '#4b5563', fontSize: '1.125rem' }}>
            Reviews, demos, releases, and news — all in one place.
          </p>
        </header>

        <div className="news-source-filter" style={{ marginBottom: 28 }}>
          <p
            style={{
              fontSize: 11,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: '#9ca3af',
              marginBottom: 10,
              fontFamily: 'Nunito, sans-serif'
            }}
          >
            Filter by source
          </p>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8
            }}
          >
            {SOURCE_FILTER_OPTIONS.map((opt) => {
              const active = sourceFilter === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setSourceFilter(opt.key)}
                  className={active ? 'news-source-filter-pill news-source-filter-pill--active' : 'news-source-filter-pill'}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {error && (
          <div
            style={{
              marginBottom: 24,
              padding: 16,
              borderRadius: 8,
              color: '#991b1b',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca'
            }}
            role="alert"
          >
            {error}
          </div>
        )}

        {loading && (
          <p style={{ color: '#4b5563', fontFamily: 'Nunito, sans-serif' }}>Loading articles…</p>
        )}

        {!loading && !error && articles.length === 0 && (
          <p style={{ color: '#4b5563' }}>No articles yet. Check back after the next scheduled feed sync.</p>
        )}

        {!loading && !error && articles.length > 0 && filteredArticles.length === 0 && (
          <p style={{ color: '#4b5563', marginBottom: 16 }}>
            No articles for this source. Try &ldquo;All sources&rdquo; or pick another feed.
          </p>
        )}

        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 28 }}>
          {!loading &&
            filteredArticles.map((article) => {
              const brands = Array.isArray(article.extractedBrands)
                ? article.extractedBrands
                : [];
              const brandKeysForCarousel = brands.filter(
                (k) => brandStatsMap[k]?.displayName
              );
              const hasImage = !!article.imageUrl;
              const cleanedExcerpt = stripRssBoilerplate(article.summary || '');
              const { text: excerptDisplay } = truncateSummaryForDisplay(cleanedExcerpt);
              const readHost = formatReadOnHostname(article.link);
              const sourceLabel = article.sourceName || article.sourceKey || 'News';

              return (
                <li
                  key={article.id}
                  style={{
                    backgroundColor: '#fff',
                    borderRadius: 12,
                    border: '1px solid #e8e4dc',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    overflow: 'hidden'
                  }}
                >
                  <div
                    className={
                      hasImage ? 'news-article-row news-article-row--has-image' : 'news-article-row'
                    }
                  >
                    {hasImage ? (
                      <NewsArticleThumb imageUrl={article.imageUrl} articleLink={article.link} />
                    ) : null}

                    <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                        padding: '20px 22px 18px',
                        textAlign: 'left'
                      }}
                    >
                      <div className="news-article-meta">
                        <span className="news-source-pill">{sourceLabel}</span>
                        <time
                          style={{ fontSize: 13, color: '#9ca3af' }}
                          dateTime={toJsDate(article.publishedAt)?.toISOString() || undefined}
                        >
                          {formatPublishedDate(article.publishedAt) || 'Date unknown'}
                        </time>
                      </div>

                      <h2
                        style={{
                          fontSize: '1.2rem',
                          fontWeight: 700,
                          color: '#111827',
                          marginBottom: 10,
                          lineHeight: 1.35,
                          fontFamily: 'Nunito, sans-serif'
                        }}
                      >
                        <a
                          href={article.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#111827', textDecoration: 'none' }}
                        >
                          {article.title || 'Untitled'}
                        </a>
                      </h2>

                      {cleanedExcerpt ? (
                        <p
                          style={{
                            color: '#6b7280',
                            fontSize: '0.95rem',
                            lineHeight: 1.55,
                            marginBottom: 12,
                            fontFamily: 'Nunito, sans-serif'
                          }}
                        >
                          {excerptDisplay}
                        </p>
                      ) : null}

                      {readHost ? (
                        <a
                          href={article.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="news-read-on-link"
                        >
                          Read on {readHost} →
                        </a>
                      ) : null}

                      {!user && brands.length > 0 ? (
                        <p
                          style={{
                            fontSize: 13,
                            color: '#6b7280',
                            marginTop: 14,
                            marginBottom: 0,
                            fontFamily: 'Nunito, sans-serif'
                          }}
                        >
                          <Link to="/login" style={{ color: '#5A9ECB', fontWeight: 600 }}>
                            Log in
                          </Link>{' '}
                          to see pedals on PedalxChange that match this story.
                        </p>
                      ) : null}
                    </div>
                  </div>

                  {user && brandKeysForCarousel.length > 0 && (
                    <div className="news-pedal-strip" aria-label="Matching listings on PedalxChange">
                      {brandKeysForCarousel.map((canonicalKey, idx) => {
                        const stat = brandStatsMap[canonicalKey];
                        const listings = listingsByBrandKey[canonicalKey] || [];
                        return (
                          <div
                            key={`${article.id}-${canonicalKey}`}
                            style={{
                              paddingTop: idx > 0 ? 14 : 0,
                              marginTop: idx > 0 ? 14 : 0,
                              borderTop: idx > 0 ? '1px solid #ece8df' : 'none'
                            }}
                          >
                            <BrandListingsCarousel
                              stat={stat}
                              listings={listings}
                              displayName={stat.displayName}
                              onBeforeNavigate={persistNewsPageState}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </li>
              );
            })}
        </ul>
      </div>

      {showBackToTop && (
        <button
          type="button"
          onClick={scrollToTop}
          className="back-to-top-btn"
          style={{
            position: 'fixed',
            bottom: window.innerWidth <= 768 ? '90px' : '30px',
            right: '30px',
            backgroundColor: '#5A9ECB',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#4A8AB0';
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#5A9ECB';
            e.target.style.transform = 'scale(1)';
          }}
          aria-label="Back to top"
        >
          ⌃
        </button>
      )}
    </div>
  );
};

export default NewsPage;
